/**
 * Voice utilities for Forge — mic capture (Web Speech API + Whisper fallback)
 * and TTS playback (SpeechSynthesis + OpenAI TTS fallback).
 *
 * Design:
 * - Web Speech API is instant + free but Chromium/Safari-only. Try it first.
 * - If not supported, capture audio via MediaRecorder and POST to /api/companion/transcribe.
 * - Playback: try browser SpeechSynthesis (free, robotic); user-facing "high quality"
 *   toggle fetches OpenAI TTS mp3 from /api/companion/speak.
 */
import { api, API, getToken } from "@/lib/api";


// ---------- Speech recognition (STT) ----------

function _getWebSpeechRecognition() {
  const Ctor = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  return Ctor || null;
}


export function isWebSpeechAvailable() {
  return _getWebSpeechRecognition() !== null;
}


/**
 * Listen with Web Speech API. Resolves with the transcript string.
 * Rejects with { code: "not-supported"|"denied"|"aborted"|"error", ... }.
 *
 * Returns { stop, cancel, promise } so caller can abort mid-listen.
 */
export function listenWithWebSpeech({ language = "en-US" } = {}) {
  const Ctor = _getWebSpeechRecognition();
  if (!Ctor) {
    return { stop: () => {}, cancel: () => {}, promise: Promise.reject({ code: "not-supported" }) };
  }
  const rec = new Ctor();
  rec.lang = language;
  rec.continuous = false;
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  let finalized = false;
  const promise = new Promise((resolve, reject) => {
    rec.onresult = (e) => {
      finalized = true;
      const text = Array.from(e.results).map(r => r[0].transcript).join(" ").trim();
      resolve(text);
    };
    rec.onerror = (e) => { finalized = true; reject({ code: e.error || "error" }); };
    rec.onend = () => { if (!finalized) reject({ code: "aborted" }); };
    try { rec.start(); } catch (err) { reject({ code: "start-failed", err }); }
  });
  return {
    stop: () => { try { rec.stop(); } catch (e) { console.debug("[voice] rec stop noop", e); } },
    cancel: () => { try { rec.abort(); } catch (e) { console.debug("[voice] rec abort noop", e); } },
    promise,
  };
}


/**
 * Record audio via MediaRecorder + POST to /api/companion/transcribe.
 * Returns a controller: { stop() → Promise<string>, cancel() }.
 */
export async function startRecordingForWhisper() {
  if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    throw { code: "no-media-devices" };
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
    ? "audio/webm;codecs=opus"
    : "audio/webm";
  const recorder = new MediaRecorder(stream, { mimeType: mime });
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
  recorder.start();

  const _cleanup = () => stream.getTracks().forEach(t => t.stop());

  const stop = () => new Promise((resolve, reject) => {
    recorder.onstop = async () => {
      _cleanup();
      try {
        const blob = new Blob(chunks, { type: mime });
        if (blob.size < 300) { resolve(""); return; }
        const fd = new FormData();
        fd.append("audio", blob, "clip.webm");
        const r = await fetch(`${API}/companion/transcribe`, {
          method: "POST", body: fd,
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) { reject({ code: "transcribe-failed", status: r.status }); return; }
        const j = await r.json();
        resolve(j.text || "");
      } catch (err) { reject(err); }
    };
    try { recorder.stop(); } catch (err) { _cleanup(); reject(err); }
  });

  const cancel = () => { try { recorder.stop(); } catch (e) { console.debug("[voice] recorder stop noop", e); } _cleanup(); };
  return { stop, cancel };
}


// ---------- Speech synthesis (TTS) ----------

/** Cheapest path: browser voice. Returns a stop() function. */
export function speakBrowser(text, { rate = 1.0, pitch = 1.0, lang = "en-US" } = {}) {
  if (typeof window === "undefined" || !window.speechSynthesis) return () => {};
  window.speechSynthesis.cancel();  // stop any ongoing utterance
  const u = new SpeechSynthesisUtterance(text);
  u.rate = rate; u.pitch = pitch; u.lang = lang;
  window.speechSynthesis.speak(u);
  return () => window.speechSynthesis.cancel();
}


/**
 * High-quality path: OpenAI TTS via backend. Streams MP3 into an HTMLAudioElement.
 * Returns a Promise<HTMLAudioElement> so caller can pause/stop.
 */
export async function speakHighQuality(text, { voice = "nova" } = {}) {
  const r = await fetch(`${API}/companion/speak`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ text, voice }),
  });
  if (!r.ok) throw new Error(`TTS failed (${r.status})`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio.addEventListener("ended", () => URL.revokeObjectURL(url), { once: true });
  await audio.play();
  return audio;
}


/**
 * Best-effort speak: try OpenAI TTS first, fall back to browser SpeechSynthesis
 * so Forge is never silent. Returns `{ stop() }` for cancellation.
 * `onEnd` callback fires when playback completes (either engine).
 * Never rejects — safe to call from useEffect.
 */
export async function speakForge(text, { voice = "nova", onEnd } = {}) {
  if (!text || typeof text !== "string") { onEnd?.(); return { stop: () => {} }; }
  const trimmed = text.trim();
  if (trimmed.length < 2) { onEnd?.(); return { stop: () => {} }; }
  try {
    const audio = await speakHighQuality(trimmed, { voice });
    if (onEnd) audio.addEventListener("ended", onEnd, { once: true });
    return {
      stop: () => { try { audio.pause(); audio.currentTime = 0; } catch { /* noop */ } onEnd?.(); },
    };
  } catch (err) {
    console.debug("[voice] HQ TTS failed, falling back to browser TTS", err?.message || err);
    // Browser SpeechSynthesis
    if (typeof window === "undefined" || !window.speechSynthesis) { onEnd?.(); return { stop: () => {} }; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(trimmed);
    u.rate = 1.0; u.pitch = 1.0; u.lang = "en-US";
    if (onEnd) u.onend = onEnd;
    window.speechSynthesis.speak(u);
    return { stop: () => { window.speechSynthesis.cancel(); onEnd?.(); } };
  }
}


export const VOICE_OPTIONS = [
  { id: "nova",    label: "Nova (energetic)" },
  { id: "alloy",   label: "Alloy (neutral)" },
  { id: "shimmer", label: "Shimmer (bright)" },
  { id: "onyx",    label: "Onyx (deep)" },
  { id: "coral",   label: "Coral (warm)" },
  { id: "echo",    label: "Echo (calm)" },
];
